import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { getCurrencySymbol, formatCurrency } from '../../utils/currency';
import {
  Search,
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  Upload,
  Download,
  Building2,
  User,
  Users,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  FileText,
  Receipt,
  ChevronRight,
  Loader2,
  AlertCircle,
  CheckCircle2,
  X,
  ArrowLeft,
  Clock,
  Banknote,
  Eye,
  CheckSquare,
  Square,
  XCircle
} from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { ContactTaxAttributes } from '../tax-catalogue/ContactTaxAttributes';

const STATUS_TONE = {
  paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  partial: 'bg-sky-100 text-sky-700 border-sky-200',
  partially_paid: 'bg-sky-100 text-sky-700 border-sky-200',
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  issued: 'bg-amber-100 text-amber-700 border-amber-200',
  sent: 'bg-amber-100 text-amber-700 border-amber-200',
  overdue: 'bg-rose-100 text-rose-700 border-rose-200',
  draft: 'bg-slate-100 text-slate-600 border-slate-200',
  voided: 'bg-slate-100 text-slate-500 border-slate-200',
};
const STRIPE_TONE = { paid: 'bg-emerald-400', overdue: 'bg-rose-400', draft: 'bg-slate-300', voided: 'bg-slate-300' };

const HistoryRow = ({ number, date, amount, status, meta, onClick, testId, currencySymbol }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="w-full text-left relative flex items-center gap-3 pl-4 pr-3 py-2.5 rounded-xl bg-white border border-slate-200/80 hover:border-[#108981]/50 hover:shadow-sm transition-all group"
  >
    <span className={`absolute left-0 inset-y-2 w-1 rounded-full ${STRIPE_TONE[status] || 'bg-amber-400'}`} />
    <span className="min-w-0 flex-1">
      <span className="block font-semibold text-sm text-slate-900 truncate">{number}</span>
      <span className="block text-[11px] text-slate-500 truncate">{date}{meta ? ` · ${meta}` : ''}</span>
    </span>
    <span className="text-right shrink-0">
      <span className="block font-mono font-semibold text-sm tabular-nums text-slate-900">{currencySymbol}{Number(amount || 0).toLocaleString()}</span>
      <span className={`inline-block mt-0.5 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full border ${STATUS_TONE[status] || STATUS_TONE.pending}`}>{String(status || '').replace('_', ' ')}</span>
    </span>
    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-[#108981] shrink-0" />
  </button>
);

const HistoryEmpty = ({ text }) => (
  <div className="text-center py-8 rounded-xl border border-dashed border-slate-200 bg-slate-50/60">
    <p className="text-sm text-slate-500">{text}</p>
  </div>
);

const ContactsTab = ({ openContactId } = {}) => {
  const navigate = useNavigate();
  const { business } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  // iter 755 - CN/DN history per contact (loaded lazily on View Details)
  const [contactCreditNotes, setContactCreditNotes] = useState([]);
  const [contactDebitNotes, setContactDebitNotes] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    contact_type: 'customer',
    email: '',
    phone: '',
    company_name: '',
    tax_id: '',
    tax_attributes: {},
    payment_terms: 30,
    credit_limit: '',
    notes: '',
    address: {
      street: '',
      city: '',
      state: '',
      postal_code: '',
      country: business?.country || 'NG'
    },
    bank_details: {
      bank_name: '',
      account_name: '',
      account_number: ''
    }
  });

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('contact_type', filterType);
      if (searchTerm) params.append('search', searchTerm);
      
      const qs = params.toString();
      const response = await axios.get(`${API}/contacts${qs ? `?${qs}` : ''}`);
      setContacts(response.data.contacts || []);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [business?.id, filterType, searchTerm]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const handleAddContact = async () => {
    if (!formData.name.trim()) {
      toast.error('Contact name is required');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        credit_limit: formData.credit_limit ? parseFloat(formData.credit_limit) : null
      };
      
      if (editMode && selectedContact) {
        await axios.put(`${API}/contacts/${selectedContact.id}`, payload);
        toast.success('Contact updated successfully');
      } else {
        await axios.post(`${API}/contacts`, payload);
        toast.success('Contact created successfully');
      }
      
      setShowAddModal(false);
      resetForm();
      fetchContacts();
    } catch (error) {
      console.error('Failed to save contact:', error);
      toast.error(error.response?.data?.detail || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  // Sep 2026 · global search deep link: open the contact passed via ?contact=<id>
  useEffect(() => {
    if (openContactId) handleViewContact({ id: openContactId });
  }, [openContactId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleViewContact = async (contact) => {
    try {
      const response = await axios.get(`${API}/contacts/${contact.id}`);
      setSelectedContact(response.data);
      setShowDetailModal(true);
      // iter 755 - fetch CN + DN history for this contact in parallel.
      // Customer CNs query by customer_id; vendor DNs query by vendor_id.
      try {
        const [cnRes, dnRes] = await Promise.all([
          axios.get(`${API}/credit-notes?customer_id=${contact.id}`).catch(() => ({ data: [] })),
          axios.get(`${API}/debit-notes?vendor_id=${contact.id}`).catch(() => ({ data: [] })),
        ]);
        setContactCreditNotes(Array.isArray(cnRes.data) ? cnRes.data : (cnRes.data?.items || []));
        setContactDebitNotes(Array.isArray(dnRes.data) ? dnRes.data : (dnRes.data?.items || []));
      } catch (notesErr) {
        console.error('Failed to fetch CN/DN history:', notesErr);
        setContactCreditNotes([]);
        setContactDebitNotes([]);
      }
    } catch (error) {
      console.error('Failed to fetch contact details:', error);
      toast.error('Failed to load contact details');
    }
  };

  const handleEditContact = (contact) => {
    setFormData({
      name: contact.name || '',
      contact_type: contact.contact_type || 'customer',
      email: contact.email || '',
      phone: contact.phone || '',
      company_name: contact.company_name || '',
      tax_id: contact.tax_id || '',
      tax_attributes: contact.tax_attributes || {},
      payment_terms: contact.payment_terms || 30,
      credit_limit: contact.credit_limit || '',
      notes: contact.notes || '',
      address: contact.address || {
        street: '',
        city: '',
        state: '',
        postal_code: '',
        country: business?.country || 'NG'
      },
      bank_details: contact.bank_details || {
        bank_name: '',
        account_name: '',
        account_number: ''
      }
    });
    setSelectedContact(contact);
    setEditMode(true);
    setShowDetailModal(false);
    setShowAddModal(true);
  };

  const handleDeleteContact = async (contact) => {
    if (!confirm(`Are you sure you want to deactivate ${contact.name}?`)) return;
    
    try {
      await axios.delete(`${API}/contacts/${contact.id}`);
      toast.success('Contact deactivated');
      fetchContacts();
      setShowDetailModal(false);
    } catch (error) {
      console.error('Failed to delete contact:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete contact');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImporting(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', importFile);
      
      const response = await axios.post(
        `${API}/contacts/import`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
      
      setShowImportModal(false);
      setImportFile(null);
      fetchContacts();
    } catch (error) {
      console.error('Import failed:', error);
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/contacts/export/template`);
      const blob = new Blob([response.data.template], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contacts_template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Bulk selection handlers
  const toggleSelectContact = (contactId) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(contactId)) {
      newSelected.delete(contactId);
    } else {
      newSelected.add(contactId);
    }
    setSelectedIds(newSelected);
    setShowBulkActions(newSelected.size > 0);
  };

  const selectAllContacts = () => {
    if (selectedIds.size === contacts.length) {
      setSelectedIds(new Set());
      setShowBulkActions(false);
    } else {
      setSelectedIds(new Set(contacts.map(c => c.id)));
      setShowBulkActions(true);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/contacts/bulk/action`,
        {
          contact_ids: Array.from(selectedIds),
          action: action
        }
      );
      
      toast.success(response.data.message);
      setSelectedIds(new Set());
      setShowBulkActions(false);
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} contacts`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkExport = async () => {
    setBulkActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/contacts/bulk/export`,
        { contact_ids: selectedIds.size > 0 ? Array.from(selectedIds) : null }
      );
      
      const blob = new Blob([response.data.csv_content], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${response.data.count} contacts`);
    } catch (error) {
      toast.error('Failed to export contacts');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contact_type: 'customer',
      email: '',
      phone: '',
      company_name: '',
      tax_id: '',
    tax_attributes: {},
      payment_terms: 30,
      credit_limit: '',
      notes: '',
      address: {
        street: '',
        city: '',
        state: '',
        postal_code: '',
        country: business?.country || 'NG'
      },
      bank_details: {
        bank_name: '',
        account_name: '',
        account_number: ''
      }
    });
    setEditMode(false);
    setSelectedContact(null);
  };

  const getContactTypeIcon = (type) => {
    switch (type) {
      case 'customer': return <User className="w-4 h-4 text-blue-500" />;
      case 'vendor': return <Building2 className="w-4 h-4 text-orange-500" />;
      default: return <Users className="w-4 h-4 text-purple-500" />;
    }
  };

  const getContactTypeBadge = (type) => {
    const colors = {
      customer: 'bg-blue-100 text-blue-700',
      vendor: 'bg-orange-100 text-orange-700',
      other: 'bg-purple-100 text-purple-700'
    };
    return colors[type] || colors.other;
  };

  const currencySymbol = getCurrencySymbol(business?.currency || 'NGN');

  return (
    <div className="space-y-6" data-testid="contacts-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold">Contacts</h2>
          <p className="text-muted-foreground">Manage your customers, vendors, and other contacts</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowImportModal(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          <Button onClick={() => { resetForm(); setShowAddModal(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Add Contact
          </Button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search contacts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="contact-search"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[180px]" data-testid="contact-filter">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Contacts</SelectItem>
            <SelectItem value="customer">Customers</SelectItem>
            <SelectItem value="vendor">Vendors</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk Actions Bar */}
      <AnimatePresence>
        {showBulkActions && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-[#283F8B] text-white rounded-lg p-3 mb-4 flex items-center justify-between"
            data-testid="bulk-actions-bar"
          >
            <div className="flex items-center gap-3">
              <CheckSquare className="w-5 h-5" />
              <span className="font-medium">{selectedIds.size} selected</span>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10"
                onClick={() => { setSelectedIds(new Set()); setShowBulkActions(false); }}
              >
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10"
                onClick={handleBulkExport}
                disabled={bulkActionLoading}
                data-testid="bulk-export-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                Export
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10"
                onClick={() => handleBulkAction('deactivate')}
                disabled={bulkActionLoading}
                data-testid="bulk-deactivate-btn"
              >
                <XCircle className="w-4 h-4 mr-1" />
                Deactivate
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-200 hover:bg-red-500/20"
                onClick={() => handleBulkAction('delete')}
                disabled={bulkActionLoading}
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
              {bulkActionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Contacts List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : contacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Users className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="font-medium text-lg mb-2">No contacts found</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm ? 'Try a different search term' : 'Add your first contact to get started'}
              </p>
              <Button onClick={() => { resetForm(); setShowAddModal(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Contact
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {/* Select All Header */}
              <div className="flex items-center gap-3 p-3 bg-muted/30 border-b">
                <Checkbox
                  checked={selectedIds.size === contacts.length && contacts.length > 0}
                  onCheckedChange={selectAllContacts}
                  data-testid="select-all-checkbox"
                />
                <span className="text-sm text-muted-foreground">
                  {selectedIds.size === contacts.length ? 'Deselect all' : 'Select all'}
                </span>
              </div>
              
              {contacts.map((contact) => (
                <div
                  key={contact.id}
                  className={`flex items-center justify-between p-4 hover:bg-muted/50 transition-colors cursor-pointer ${selectedIds.has(contact.id) ? 'bg-[#283F8B]/5' : ''}`}
                  data-testid={`contact-row-${contact.id}`}
                >
                  <div className="flex items-center gap-4">
                    <Checkbox
                      checked={selectedIds.has(contact.id)}
                      onCheckedChange={() => toggleSelectContact(contact.id)}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`contact-checkbox-${contact.id}`}
                    />
                    <div 
                      className="flex items-center gap-4 flex-1"
                      onClick={() => handleViewContact(contact)}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getContactTypeBadge(contact.contact_type)}`}>
                        {getContactTypeIcon(contact.contact_type)}
                      </div>
                      <div>
                        <p className="font-medium">{contact.name}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          {contact.company_name && <span>{contact.company_name}</span>}
                          {contact.email && (
                            <>
                              {contact.company_name && <span>•</span>}
                              <span>{contact.email}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
                    <Badge className={getContactTypeBadge(contact.contact_type)}>
                      {contact.contact_type}
                    </Badge>
                    {contact.outstanding_balance > 0 && (
                      <Badge variant="outline" className="text-amber-600 border-amber-300">
                        {currencySymbol}{contact.outstanding_balance.toLocaleString()} outstanding
                      </Badge>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewContact(contact); }}>
                          <Eye className="w-4 h-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEditContact(contact); }}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={(e) => { e.stopPropagation(); handleDeleteContact(contact); }}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Contact Modal */}
      <Dialog open={showAddModal} onOpenChange={(open) => { setShowAddModal(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Edit Contact' : 'Add New Contact'}</DialogTitle>
            <DialogDescription>
              {editMode ? 'Update contact information' : 'Create a new customer, vendor, or other contact'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <Label>Contact Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe or Company Name"
                  data-testid="contact-name-input"
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label>Contact Type *</Label>
                <Select 
                  value={formData.contact_type} 
                  onValueChange={(v) => setFormData({ ...formData, contact_type: v })}
                >
                  <SelectTrigger data-testid="contact-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="vendor">Vendor</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contact@example.com"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+234 XXX XXX XXXX"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Company Name</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="Company Ltd"
                />
              </div>
              <div>
                <Label>Tax ID / VAT Number</Label>
                <Input
                  value={formData.tax_id}
                  onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                  placeholder="12345678-0001"
                  data-testid="contact-tax-id-input"
                />
              </div>
            </div>

            <ContactTaxAttributes value={formData.tax_attributes || {}} onChange={(v) => setFormData({ ...formData, tax_attributes: v })} contactType={formData.contact_type} />

            {/* Address */}
            <div className="space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Address
              </h4>
              <div>
                <Label>Street Address</Label>
                <Input
                  value={formData.address?.street || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    address: { ...formData.address, street: e.target.value } 
                  })}
                  placeholder="123 Main Street"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>City</Label>
                  <Input
                    value={formData.address?.city || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      address: { ...formData.address, city: e.target.value } 
                    })}
                    placeholder="Lagos"
                  />
                </div>
                <div>
                  <Label>State / Region</Label>
                  <Input
                    value={formData.address?.state || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      address: { ...formData.address, state: e.target.value } 
                    })}
                    placeholder="Lagos"
                  />
                </div>
              </div>
              <div>
                <Label>Country</Label>
                <Input
                  value={formData.address?.country || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    address: { ...formData.address, country: e.target.value } 
                  })}
                  placeholder="Nigeria (or ISO code, e.g. GB)"
                  data-testid="contact-address-country"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Used by the tax engine: customers abroad are treated as exports; suppliers abroad may attract reverse charge.</p>
              </div>
            </div>

            {/* Payment Terms */}
            <div className="space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Payment Terms
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Payment Terms (days)</Label>
                  <Select 
                    value={String(formData.payment_terms)} 
                    onValueChange={(v) => setFormData({ ...formData, payment_terms: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Due on receipt</SelectItem>
                      <SelectItem value="7">Net 7</SelectItem>
                      <SelectItem value="14">Net 14</SelectItem>
                      <SelectItem value="30">Net 30</SelectItem>
                      <SelectItem value="45">Net 45</SelectItem>
                      <SelectItem value="60">Net 60</SelectItem>
                      <SelectItem value="90">Net 90</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Credit Limit ({currencySymbol})</Label>
                  <Input
                    type="number"
                    value={formData.credit_limit}
                    onChange={(e) => setFormData({ ...formData, credit_limit: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            {/* Bank Details (for vendors) */}
            {formData.contact_type === 'vendor' && (
              <div className="space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <Banknote className="w-4 h-4" />
                  Bank Details
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Bank Name</Label>
                    <Input
                      value={formData.bank_details?.bank_name || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        bank_details: { ...formData.bank_details, bank_name: e.target.value } 
                      })}
                      placeholder="First Bank"
                    />
                  </div>
                  <div>
                    <Label>Account Name</Label>
                    <Input
                      value={formData.bank_details?.account_name || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        bank_details: { ...formData.bank_details, account_name: e.target.value } 
                      })}
                      placeholder="Account holder name"
                    />
                  </div>
                </div>
                <div>
                  <Label>Account Number</Label>
                  <Input
                    value={formData.bank_details?.account_number || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      bank_details: { ...formData.bank_details, account_number: e.target.value } 
                    })}
                    placeholder="0123456789"
                  />
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about this contact..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddModal(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleAddContact} disabled={saving} data-testid="contact-save-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editMode ? 'Update Contact' : 'Create Contact'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="w-[94vw] sm:w-full max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6" data-testid="contact-detail-dialog">
          {selectedContact && (
            <>
              <DialogHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pr-6">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getContactTypeBadge(selectedContact.contact_type)}`}>
                      {getContactTypeIcon(selectedContact.contact_type)}
                    </div>
                    <div>
                      <DialogTitle className="text-xl">{selectedContact.name}</DialogTitle>
                      <DialogDescription>
                        {selectedContact.company_name || selectedContact.contact_type}
                      </DialogDescription>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEditContact(selectedContact)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="space-y-5 py-3 min-w-0 w-full">
                {/* Contact Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
                  {selectedContact.email && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span>{selectedContact.email}</span>
                    </div>
                  )}
                  {selectedContact.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span>{selectedContact.phone}</span>
                    </div>
                  )}
                  {selectedContact.tax_id && (
                    <div className="flex items-center gap-2 text-sm">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <span>Tax ID: {selectedContact.tax_id}</span>
                    </div>
                  )}
                  {selectedContact.payment_terms && (
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span>Net {selectedContact.payment_terms} days</span>
                    </div>
                  )}
                </div>

                {/* Financial Summary - compact 3-up strip */}
                <div className="grid grid-cols-3 gap-2" data-testid="contact-financial-summary">
                  {[
                    { label: 'Invoiced', value: selectedContact.total_invoiced, tone: 'text-[#283F8B]', bg: 'bg-[#283F8B]/5 ring-[#283F8B]/15' },
                    { label: 'Billed', value: selectedContact.total_billed, tone: 'text-orange-600', bg: 'bg-orange-50 ring-orange-200/60' },
                    { label: 'Outstanding', value: selectedContact.outstanding_balance, tone: 'text-amber-600', bg: 'bg-amber-50 ring-amber-200/60' },
                  ].map((k) => (
                    <div key={k.label} className={`rounded-xl ring-1 px-2.5 py-2 min-w-0 ${k.bg}`}>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 truncate">{k.label}</p>
                      <p className={`text-sm sm:text-lg font-extrabold tabular-nums truncate ${k.tone}`} title={`${currencySymbol}${(k.value || 0).toLocaleString()}`}>
                        {currencySymbol}{(k.value || 0).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Transaction History */}
                {selectedContact.transactions && (
                  <div className="min-w-0">
                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-2"><Clock className="w-4 h-4 text-[#108981]" /> Transaction History</h4>
                    <Tabs defaultValue={(selectedContact.transactions.bills?.length || 0) > (selectedContact.transactions.invoices?.length || 0) ? 'bills' : 'invoices'}>
                      <TabsList className="w-full flex overflow-x-auto justify-start h-auto flex-nowrap gap-1 p-1 rounded-xl bg-slate-100 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="contact-history-tabs">
                        {[
                          ['invoices', 'Invoices', selectedContact.transactions.invoices?.length || 0, undefined],
                          ['bills', 'Bills', selectedContact.transactions.bills?.length || 0, undefined],
                          ['credit_notes', 'Credit notes', contactCreditNotes.filter(n => n.status !== 'voided').length, 'contact-credit-notes-tab'],
                          ['debit_notes', 'Debit notes', contactDebitNotes.filter(n => n.status !== 'voided').length, 'contact-debit-notes-tab'],
                        ].map(([v, label, count, tid]) => (
                          <TabsTrigger key={v} value={v} data-testid={tid} className="shrink-0 rounded-lg text-xs px-2.5 py-1.5 gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm">
                            {label}<span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-slate-200/80 text-[10px] font-bold text-slate-600 px-1">{count}</span>
                          </TabsTrigger>
                        ))}
                      </TabsList>
                      <TabsContent value="invoices" className="mt-3">
                        <div className="max-h-[260px] overflow-y-auto space-y-1.5 pr-0.5">
                          {selectedContact.transactions.invoices?.length > 0 ? (
                            selectedContact.transactions.invoices.map((inv) => (
                              <HistoryRow key={inv.id} number={inv.invoice_number} date={new Date(inv.date).toLocaleDateString()} amount={inv.amount}
                                status={inv.status} currencySymbol={currencySymbol} onClick={() => navigate(`/invoices/${inv.id}`)} testId={`contact-invoice-${inv.id}`} />
                            ))
                          ) : <HistoryEmpty text="No invoices for this contact yet" />}
                        </div>
                      </TabsContent>
                      <TabsContent value="bills" className="mt-3">
                        <div className="max-h-[260px] overflow-y-auto space-y-1.5 pr-0.5">
                          {selectedContact.transactions.bills?.length > 0 ? (
                            selectedContact.transactions.bills.map((bill) => (
                              <HistoryRow key={bill.id} number={bill.bill_number} date={new Date(bill.date).toLocaleDateString()} amount={bill.amount}
                                status={bill.status} currencySymbol={currencySymbol} onClick={() => navigate(`/bills/${bill.id}`)} testId={`contact-bill-${bill.id}`} />
                            ))
                          ) : <HistoryEmpty text="No bills for this contact yet" />}
                        </div>
                      </TabsContent>
                      {/* iter 755 - Credit Notes for this customer */}
                      <TabsContent value="credit_notes" className="mt-3">
                        <div className="max-h-[260px] overflow-y-auto pr-0.5">
                          {contactCreditNotes.length > 0 ? (
                            <div className="space-y-2">
                              {contactCreditNotes.map((cn) => {
                                const voided = cn.status === 'voided';
                                const runningTotal = contactCreditNotes
                                  .filter(n => n.status !== 'voided')
                                  .reduce((a, n) => a + parseFloat(n.amount || 0), 0);
                                return (
                                  <div key={cn.id} className={`flex items-center justify-between p-3 rounded-lg ${voided ? 'bg-muted/30 opacity-60' : 'bg-orange-50 dark:bg-orange-950/20'}`} data-testid={`contact-cn-${cn.id}`}>
                                    <div className="min-w-0 flex-1">
                                      <p className="font-mono text-sm font-medium">{cn.credit_note_number}</p>
                                      <p className="text-xs text-muted-foreground truncate">
                                        {cn.credit_note_date} · {cn.invoice_number ? `vs ${cn.invoice_number}` : ''} · {cn.reason || 'No reason'}
                                      </p>
                                    </div>
                                    <div className="text-right ml-2">
                                      <p className="font-semibold">{currencySymbol}{parseFloat(cn.amount || 0).toLocaleString()}</p>
                                      <Badge variant={voided ? 'secondary' : 'default'} className="text-xs">
                                        {voided ? 'Voided' : 'Issued'}
                                      </Badge>
                                    </div>
                                  </div>
                                );
                              })}
                              <div className="flex items-center justify-between p-2 pt-3 border-t text-sm font-medium">
                                <span>Active total</span>
                                <span data-testid="contact-cn-running-total">
                                  {currencySymbol}{contactCreditNotes.filter(n => n.status !== 'voided').reduce((a, n) => a + parseFloat(n.amount || 0), 0).toLocaleString()}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <p className="text-center text-muted-foreground py-8">No credit notes issued to this customer</p>
                          )}
                        </div>
                      </TabsContent>
                      {/* iter 755 - Debit Notes for this vendor */}
                      <TabsContent value="debit_notes" className="mt-3">
                        <div className="max-h-[260px] overflow-y-auto pr-0.5">
                          {contactDebitNotes.length > 0 ? (
                            <div className="space-y-2">
                              {contactDebitNotes.map((dn) => {
                                const voided = dn.status === 'voided';
                                return (
                                  <div key={dn.id} className={`flex items-center justify-between p-3 rounded-lg ${voided ? 'bg-muted/30 opacity-60' : 'bg-blue-50 dark:bg-blue-950/20'}`} data-testid={`contact-dn-${dn.id}`}>
                                    <div className="min-w-0 flex-1">
                                      <p className="font-mono text-sm font-medium">{dn.debit_note_number}</p>
                                      <p className="text-xs text-muted-foreground truncate">
                                        {dn.debit_note_date} · {dn.bill_reference ? `vs ${dn.bill_reference}` : ''} · {dn.reason || 'No reason'}
                                      </p>
                                    </div>
                                    <div className="text-right ml-2">
                                      <p className="font-semibold">{currencySymbol}{parseFloat(dn.amount || 0).toLocaleString()}</p>
                                      <Badge variant={voided ? 'secondary' : 'default'} className="text-xs">
                                        {voided ? 'Voided' : 'Issued'}
                                      </Badge>
                                    </div>
                                  </div>
                                );
                              })}
                              <div className="flex items-center justify-between p-2 pt-3 border-t text-sm font-medium">
                                <span>Active total</span>
                                <span data-testid="contact-dn-running-total">
                                  {currencySymbol}{contactDebitNotes.filter(n => n.status !== 'voided').reduce((a, n) => a + parseFloat(n.amount || 0), 0).toLocaleString()}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <p className="text-center text-muted-foreground py-8">No debit notes issued to this vendor</p>
                          )}
                        </div>
                      </TabsContent>
                    </Tabs>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Contacts</DialogTitle>
            <DialogDescription>
              Upload a CSV file to import multiple contacts at once
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="max-w-xs mx-auto"
              />
              <p className="text-sm text-muted-foreground mt-2">
                {importFile ? importFile.name : 'Select a CSV file'}
              </p>
            </div>
            
            <Button variant="outline" className="w-full" onClick={downloadTemplate}>
              <Download className="w-4 h-4 mr-2" />
              Download CSV Template
            </Button>
            
            <p className="text-xs text-muted-foreground">
              Required columns: name, type (customer/vendor/other). Optional: email, phone, company, address, city, state, postal_code, country, tax_id, payment_terms, credit_limit, notes
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowImportModal(false); setImportFile(null); }}>
              Cancel
            </Button>
            <Button onClick={handleImport} disabled={importing || !importFile}>
              {importing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Import Contacts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactsTab;
